import type { ColorTheme } from '../styles/tokens';
import { blueBtnStyle as sharedBlueBtnStyle, inputStyle as sharedInputStyle } from '../styles/admin-styles';
import { SECTORS } from '../config/defaults';
import type { OrgSector } from '../api/keycloak-admin/org-config';
import type { StoredSectorTemplate } from '../api/keycloak-admin/org-config';

interface OrgSetupHeaderProps {
  orgName: string;
  sector: OrgSector | '';
  branchOrType: string;
  isDark: boolean;
  colors: ColorTheme;
  templates: StoredSectorTemplate[];
  onOrgNameChange: (name: string) => void;
  onSectorChange: (sector: OrgSector) => void;
  onTypeChange: (typeName: string) => void;
  onApplyTemplate: () => void;
  hasExistingTree: boolean;
}

/** Org setup bar: sector/type selector, org name, and template button */
export function OrgSetupHeader({
  orgName, sector, branchOrType, isDark, colors, templates,
  onOrgNameChange, onSectorChange, onTypeChange, onApplyTemplate, hasExistingTree,
}: OrgSetupHeaderProps): React.JSX.Element {
  const sectorTemplates = sector ? templates.filter((t) => t.sector === sector) : [];
  const typeOptions = sectorTemplates.map((t) => t.typeName);

  const iStyle: React.CSSProperties = {
    ...sharedInputStyle(isDark, colors),
    height: '36px',
    padding: '0 12px',
  };

  const btnDisabled = !sector || !branchOrType;
  const btnStyle = sharedBlueBtnStyle(isDark, btnDisabled);

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
      padding: '12px 0', marginBottom: '16px',
      borderBottom: `1px solid ${colors.border}`,
    }}>
      <select
        className="vx-full-field"
        value={sector}
        onChange={(e) => onSectorChange(e.target.value as OrgSector)}
        style={{ ...iStyle, width: '180px', cursor: 'pointer' }}
      >
        <option value="">Select Sector</option>
        {SECTORS.map((s) => (
          <option key={s} value={s}>{formatSector(s)}</option>
        ))}
      </select>

      <select
        className="vx-full-field"
        value={branchOrType}
        onChange={(e) => onTypeChange(e.target.value)}
        disabled={!sector}
        style={{ ...iStyle, width: '180px', cursor: 'pointer', opacity: sector ? 1 : 0.5 }}
      >
        <option value="">Select Type</option>
        {typeOptions.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>

      <input
        className="vx-full-field"
        type="text"
        placeholder="Organization name"
        value={orgName}
        onChange={(e) => onOrgNameChange(e.target.value)}
        style={{ ...iStyle, flex: 1, minWidth: '200px' }}
      />

      <button
        onClick={onApplyTemplate}
        disabled={btnDisabled}
        style={btnStyle}
      >
        {hasExistingTree ? 'Replace with Template' : 'Apply Template'}
      </button>
    </div>
  );
}

/** Format sector slug to display label */
function formatSector(sector: string): string {
  return sector.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
